import { DecimalPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as XLSX from 'xlsx';
import { FormsModule } from '@angular/forms';
import { SaleReportComponent } from '../sale-report/sale-report.component';
import { PurchaseComponent } from '../purchase/purchase.component';
import { OldMetalComponent } from '../old-metal/old-metal.component';
import { OrdersComponent } from '../orders/orders.component';
import { UdhaarComponent } from '../udhaar/udhaar.component';
import { ExpenseComponent } from '../expense/expense.component';
import { CashflowComponent } from '../cashflow/cashflow.component';
import { FirmService } from '../../../../Services/firm.service';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-daybook-layout',
  standalone: true,
  imports: [
    FormsModule,
    NgFor,
    NgIf,
    NgClass,
    SaleReportComponent,
    PurchaseComponent,
    OldMetalComponent,
    OrdersComponent,
    UdhaarComponent,
    ExpenseComponent,
    CashflowComponent,
  ],
  templateUrl: './daybook-layout.component.html',
  styleUrl: './daybook-layout.component.css',
})
export class DaybookLayoutComponent implements OnInit, OnDestroy {
  @ViewChild('cashflowComp') cashflowComp!: CashflowComponent;
  @ViewChild('udhaarComp') udhaarComp!: UdhaarComponent;
  @ViewChild('ordersComp') ordersComp!: OrdersComponent;
  @ViewChild('oldMetalComp') oldMetalComp!: OldMetalComponent;
  @ViewChild('purchaseComp') purchaseComp!: PurchaseComponent;
  @ViewChild('expenseComp') expenseComp!: ExpenseComponent;
  @ViewChild('saleComp') saleComp!: SaleReportComponent;

  firms: any[] = [];
  selectedFirmId: any = '';
  isGst: boolean = true;
  fromDate: string = new Date().toISOString().split('T')[0];

  toDate: string = new Date().toISOString().split('T')[0];

  private firmSubscription: Subscription | null = null;

  constructor(
    private firmService: FirmService,
    private firmSelectionService: FirmSelectionService,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.loadFirms();

    // Subscribe to global firm selection for default and restriction
    this.firmSubscription = this.firmSelectionService.selectedFirmName$.subscribe(firm => {
      if (firm) {
        this.selectedFirmId = firm.id;
        // Re-filter firms just in case
        this.loadFirms();
        this.updateGstStatus();
        this.onFilterChange();
      }
    });

    // Initial check from service
    const currentFirm = this.firmSelectionService.getselectedFirmName();
    if (currentFirm) {
      this.selectedFirmId = currentFirm.id;
      this.updateGstStatus();
      this.onFilterChange();
    }

    // Handle initial tab from query parameter or route parameter
    this.route.queryParams.subscribe(params => {
      if (params['tab']) {
        this.activeTab = params['tab'];
      }
    });

    this.route.params.subscribe(params => {
      if (params['tab']) {
        this.activeTab = params['tab'];
      }
    });
  }

  ngOnDestroy(): void {
    if (this.firmSubscription) {
      this.firmSubscription.unsubscribe();
    }
  }

  loadFirms() {
    this.firmService.getFirms().subscribe({
      next: (res: any) => {
        const allFirms = res.data || [];
        const currentFirm = this.firmSelectionService.getselectedFirmName();

        if (currentFirm) {
          // Filter to show only the logged-in/selected firm
          this.firms = allFirms.filter((f: any) => f.id == currentFirm.id);
          if (this.firms.length > 0) {
            this.selectedFirmId = this.firms[0].id;
          }
        } else {
          this.firms = allFirms;
        }

        if (this.selectedFirmId) {
          this.updateGstStatus();
          this.onFilterChange();
        }
      },
      error: (err) => console.error('Error loading firms', err),
    });
  }

  onFilterChange() {
    this.updateGstStatus();
    // This will be handled by child components via Input/Output or shared service if needed.
    // For now, we'll rely on child components' ngOnChanges if we pass these as inputs.
  }

  updateGstStatus() {
    if (!this.selectedFirmId) {
      this.isGst = true; // Show all columns for "All Firms"
      return;
    }
    const firm = this.firms.find(f => f.id == this.selectedFirmId);
    // Check for both 'registered' (seeder) and 'yes' or '1' (common patterns)
    this.isGst = firm ? (firm.gst_status === 'registered' || firm.gst_status === 'yes' || firm.gst_status === '1') : true;
  }



  // Panels
  showPanelDropdown = false;
  panels = [
    'All Panels',
    'Purchase Details',
    'Raw Purchase Details',
    'Sell Details',
    'Sell Return Details',
    'Raw Sell Details',
    'Old Metal (Receive/Purchase/Jama) Details',
    'Old Metal (Pay/Sell/Issue) Details',
    'New Order Details',
    'Payment Details',
    'Received Details',
    'Loan Added New',
    'Additional Principal',
    'Released Additional Principal',
    'Money Deposit',
    'Loan Released',
    'Transferred Transactions',
    'Transferred Loan New',
    'Transferred Loan Released',
    'Moneylender Loan New',
    'Moneylender Money Deposit',
    'Moneylender Loan Released',
    'Udhaar Added',
    'Udhaar Deposit',
    'Deposit Added',
    'Withdraw Added',
    'Scheme Added',
    'Scheme Return',
    'Advance Money Add',
    'Advance Return Money',
    'Daily Transactions',
  ];

  selectedPanels = new Set<string>();

  toggleDropdown() {
    this.showPanelDropdown = !this.showPanelDropdown;
  }

  togglePanel(panel: string) {
    panel === 'All Panels'
      ? this.toggleAll()
      : this.selectedPanels.has(panel)
        ? this.selectedPanels.delete(panel)
        : this.selectedPanels.add(panel);
  }

  toggleAll() {
    this.selectedPanels.size === this.panels.length - 1
      ? this.selectedPanels.clear()
      : this.panels.forEach(
        (p) => p !== 'All Panels' && this.selectedPanels.add(p),
      );
  }

  reset() {
    this.selectedPanels.clear();
  }

  submit() {
    console.log([...this.selectedPanels]);
    this.showPanelDropdown = false;
  }

  isChecked(panel: string): boolean {
    return panel === 'All Panels'
      ? this.selectedPanels.size === this.panels.length - 1
      : this.selectedPanels.has(panel);
  }

  // for amount display
  headerBalances: any[] = [];
  balanceVisibility: { [key: number]: boolean } = {};

  onBalancesLoaded(balances: any[]) {
    this.headerBalances = balances;
    balances.forEach(b => {
      if (this.balanceVisibility[b.id] === undefined) {
        this.balanceVisibility[b.id] = false;
      }
    });
  }

  getDisplayAmount(balance: any): string {
    const amount = balance.current_balance;
    const isVisible = this.balanceVisibility[balance.id];

    return isVisible
      ? this.formatIndianCurrency(amount)
      : this.maskAmount(amount);
  }

  toggleAmount(id: number) {
    this.balanceVisibility[id] = !this.balanceVisibility[id];
  }

  private formatIndianCurrency(amount: number): string {
    return '₹ ' + amount.toLocaleString('en-IN');
  }

  private maskAmount(amount: number): string {
    return '₹ ' + amount.toLocaleString('en-IN').replace(/\d/g, 'X');
  }
  // For Tab
  activeTab: string | null = null;

  setTab(tab: string) {
    this.activeTab = tab;
  }

  get selectedFirmName(): string {
    const firm = this.firms.find(f => f.id == this.selectedFirmId);
    return firm ? firm.name : 'All Firms';
  }

  printDaybook() {
    let activeComp: any = null;
    let title = 'Daybook Summary';

    if (!this.activeTab || this.activeTab === 'cashflow') {
      activeComp = this.cashflowComp;
      title = 'Daybook Summary';
    } else if (this.activeTab === 'udhaar') {
      activeComp = this.udhaarComp;
      title = 'Udhaar Summary';
    } else if (this.activeTab === 'orders') {
      activeComp = this.ordersComp;
      title = 'Order Summary';
    } else if (this.activeTab === 'oldMetal') {
      activeComp = this.oldMetalComp;
      title = 'Metal Summary';
    } else if (this.activeTab === 'purchase') {
      activeComp = this.purchaseComp;
      title = 'Purchase Summary';
    } else if (this.activeTab === 'sale') {
      activeComp = this.saleComp;
      title = 'Sale Summary';
    }

    if (!activeComp) {
      alert('Selected section data is not currently visible.');
      return;
    }

    const transactions = (this.activeTab === 'sale') ? activeComp.sales : (activeComp.transactions || []);
    const summary = (this.activeTab === 'sale') ? activeComp.saleSummary : activeComp.summary;
    const openingBalance = (this.activeTab === 'sale') ? 0 : (activeComp.openingBalance || 0);
    const firmName = this.selectedFirmName;
    const period = `${this.fromDate} to ${this.toDate}`;

    const formatNum = (num: number) => {
      return (num || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };

    let printHtml = `
      <html>
      <head>
        <title>${title} - ${firmName}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; font-size: 11px; color: #333; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #0d5bab; padding-bottom: 10px; }
          .header h2 { margin: 0; color: #0d5bab; text-transform: uppercase; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px; font-size: 12px; }
          .info-item { font-weight: bold; }
          .info-item span { font-weight: normal; }
          
          .section-title { background: #f9f9fb; padding: 5px 10px; font-weight: bold; border: 1px solid #ddd; border-bottom: 0; display: flex; justify-content: space-between; align-items: center; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 15px; table-layout: fixed; }
          th, td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; word-wrap: break-word; }
          th { background-color: #f6f7fb; color: #0d5bab; font-size: 10px; }
          .text-right { text-align: right; }
          .text-green { color: #018f37; }
          .text-red { color: #bb0000; }
          .font-bold { font-weight: bold; }
          .tfoot { background-color: #fffafa; font-weight: bold; }
          @media print { @page { margin: 10mm; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>${title}</h2>
          <p>${firmName}</p>
        </div>
        <div class="info-grid">
          <div class="info-item">Period: <span>${period}</span></div>
          <div class="info-item" style="text-align:right">Generated On: <span>${new Date().toLocaleString('en-GB')}</span></div>
        </div>

        ${this.activeTab !== 'sale' ? `
        <div class="section-title">
          <span>${this.activeTab === 'udhaar' ? 'Udhaar Balance' : 'Opening Balance'}</span>
          <span>₹ ${formatNum(openingBalance)}</span>
        </div>
        ` : ''}
        <table>
          <thead>
            <tr>
              <th style="width: 8%">INV NO.</th>
              <th style="width: 12%">DATE</th>
              <th>${this.activeTab === 'sale' ? 'CUSTOMER' : 'FIRM'}</th>
              ${this.activeTab !== 'sale' ? '<th style="width: 10%">USER</th>' : ''}
              ${this.activeTab !== 'sale' ? '<th style="width: 10%">CITY</th>' : ''}
              ${this.activeTab === 'oldMetal' ? `
              <th class="text-right">METAL REC.</th>
              <th class="text-right">METAL UDHAR</th>
              <th class="text-right">METAL CONV.</th>
              <th class="text-right">TOTAL SALES</th>
              ` : (this.activeTab === 'sale' ? `
              <th class="text-right">TOTAL AMT</th>
              <th class="text-right">RECD AMT</th>
              <th class="text-right">DUE AMT</th>
              <th class="text-right">METAL BILLED</th>
              <th class="text-right">METAL RECD</th>
              <th class="text-right">METAL SETTLED</th>
              <th class="text-right">METAL DUE</th>
              ` : `
              <th class="text-right">CASH</th>
              ${this.isGst ? `
              <th class="text-right">BANK</th>
              <th class="text-right">CARD</th>
              <th class="text-right">ONLINE</th>
              ` : ''}
              <th class="text-right">DISC</th>
              <th class="text-right">TOTAL</th>
              `)}
            </tr>
          </thead>
          <tbody>
    `;

    if (this.activeTab === 'sale') {
      transactions.forEach((item: any) => {
        printHtml += `
          <tr>
            <td>${item.invoice_number}</td>
            <td>${new Date(item.invoice_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
            <td>${item.customer_name}</td>
            <td class="text-right font-bold text-green">${formatNum(item.total_amount)}</td>
            <td class="text-right text-green">${formatNum(item.amount_paid)}</td>
            <td class="text-right text-red">${formatNum(item.balance_due)}</td>
            <td class="text-right font-bold">${formatNum(item.total_metal_billed)}</td>
            <td class="text-right text-green">${formatNum(item.metal_received)}</td>
            <td class="text-right text-metal-settled-blue">${formatNum(item.metal_settled)}</td>
            <td class="text-right text-red font-bold">${formatNum(item.metal_due)}</td>
          </tr>
        `;
      });
    } else {
      transactions.forEach((item: any) => {
        printHtml += `
          <tr>
            <td>${item.invoice_no}</td>
            <td>${new Date(item.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
            <td>${item.firm}</td>
            <td style="color:#c19725">${item.user}</td>
            <td>${item.city}</td>
            ${this.activeTab === 'oldMetal' ? `
            <td class="text-right ${item.cash >= 0 ? 'text-green' : 'text-red'}">${formatNum(item.cash)}</td>
            <td class="text-right ${item.card >= 0 ? 'text-green' : 'text-red'}">${formatNum(item.card)}</td>
            <td class="text-right ${item.online >= 0 ? 'text-green' : 'text-red'}">${formatNum(item.online)}</td>
            <td class="text-right font-bold ${item.total >= 0 ? 'text-green' : 'text-red'}">${formatNum(item.total)}</td>
            ` : `
            <td class="text-right ${item.cash >= 0 ? 'text-green' : 'text-red'}">${formatNum(item.cash)}</td>
            ${this.isGst ? `
            <td class="text-right ${item.bank >= 0 ? 'text-green' : 'text-red'}">${formatNum(item.bank)}</td>
            <td class="text-right ${item.card >= 0 ? 'text-green' : 'text-red'}">${formatNum(item.card)}</td>
            <td class="text-right ${item.online >= 0 ? 'text-green' : 'text-red'}">${formatNum(item.online)}</td>
            ` : ''}
            <td class="text-right ${item.disc >= 0 ? 'text-green' : 'text-red'}">${formatNum(item.disc)}</td>
            <td class="text-right font-bold ${item.total >= 0 ? 'text-green' : 'text-red'}">${formatNum(item.total)}</td>
            `}
          </tr>
        `;
      });
    }

    if (transactions.length === 0) {
      printHtml += `<tr><td colspan="11" style="text-align:center; padding: 20px;">No transactions found.</td></tr>`;
    }

    const netMovement = (this.activeTab === 'sale') ? (summary.total || 0) : (summary.amount_in.total - summary.amount_out.total);
    const closingBalance = (this.activeTab === 'sale') ? (summary.total || 0) : (openingBalance + netMovement);

    printHtml += `
          </tbody>
          ${this.activeTab === 'sale' ? `
          <tfoot class="tfoot">
            <tr>
              <td colspan="3" class="text-right uppercase">Total:</td>
              <td class="text-right text-green">${formatNum(summary.total)}</td>
              <td class="text-right text-green">${formatNum(summary.amount_received)}</td>
              <td class="text-right text-red">${formatNum(summary.balance_due)}</td>
              <td class="text-right font-bold">${formatNum(summary.metal_billed)}</td>
              <td class="text-right text-green">${formatNum(summary.metal_received)}</td>
              <td class="text-right text-metal-settled-blue">${formatNum(summary.metal_settled)}</td>
              <td class="text-right text-red font-bold">${formatNum(summary.metal_due)}</td>
            </tr>
          </tfoot>
          ` : ''}
        </table>

        ${this.activeTab !== 'sale' ? `
        <div class="section-title">Transactional Details</div>
        <table>
          <thead>
            <tr style="background:#f9f9fb">
              <th>TYPE</th>
              ${this.activeTab === 'oldMetal' ? `
              <th class="text-right">METAL REC.</th>
              <th class="text-right">METAL UDHAR</th>
              <th class="text-right">METAL CONV.</th>
              <th class="text-right">TOTAL SALES</th>
              ` : (this.activeTab === 'sale' ? `
              <th class="text-right">SALE AMT</th>
              <th class="text-right">RECD AMT</th>
              <th class="text-right">DUE AMT</th>
              <th class="text-right">METAL BILLED</th>
              <th class="text-right">METAL RECD</th>
              <th class="text-right">METAL DUE</th>
              ` : `
              <th class="text-right">CASH</th>
              ${this.isGst ? `
              <th class="text-right">BANK</th>
              <th class="text-right">CARD</th>
              <th class="text-right">ONLINE</th>
              ` : ''}
              <th class="text-right">DISC</th>
              <th class="text-right">EXTRA AMT.</th>
              <th class="text-right">TOTAL</th>
              `)}
            </tr>
          </thead>
          <tbody>
            ${this.activeTab !== 'sale' ? `
            <tr>
              <td class="text-green font-bold">AMOUNT IN :</td>
              ${this.activeTab === 'oldMetal' ? `
              <td class="text-right text-green">${formatNum(summary.amount_in.cash)}</td>
              <td class="text-right text-green">${formatNum(summary.amount_in.card)}</td>
              <td class="text-right text-green">${formatNum(summary.amount_in.online)}</td>
              <td class="text-right text-green font-bold">${formatNum(summary.amount_in.total)}</td>
              ` : `
              <td class="text-right text-green">${formatNum(summary.amount_in.cash)}</td>
              ${this.isGst ? `
              <td class="text-right text-green">${formatNum(summary.amount_in.bank)}</td>
              <td class="text-right text-green">${formatNum(summary.amount_in.card)}</td>
              <td class="text-right text-green">${formatNum(summary.amount_in.online)}</td>
              ` : ''}
              <td class="text-right text-green">${formatNum(summary.amount_in.disc)}</td>
              <td class="text-right text-red">0.00</td>
              <td class="text-right text-green font-bold">${formatNum(summary.amount_in.total)}</td>
              `}
            </tr>
            <tr>
              <td class="text-red font-bold">AMOUNT OUT :</td>
              ${this.activeTab === 'oldMetal' ? `
              <td class="text-right text-red">${formatNum(summary.amount_out.cash)}</td>
              <td class="text-right text-red">${formatNum(summary.amount_out.card)}</td>
              <td class="text-right text-red">${formatNum(summary.amount_out.online)}</td>
              <td class="text-right text-red font-bold">${formatNum(summary.amount_out.total)}</td>
              ` : `
              <td class="text-right text-red">${formatNum(summary.amount_out.cash)}</td>
              ${this.isGst ? `
              <td class="text-right text-red">${formatNum(summary.amount_out.bank)}</td>
              <td class="text-right text-red">${formatNum(summary.amount_out.card)}</td>
              <td class="text-right text-red">${formatNum(summary.amount_out.online)}</td>
              ` : ''}
              <td class="text-right text-red">${formatNum(summary.amount_out.disc)}</td>
              <td class="text-right text-red">0.00</td>
              <td class="text-right text-red font-bold">${formatNum(summary.amount_out.total)}</td>
              `}
            </tr>
            <tr>
              <td class="text-red font-bold">TODAY TOTAL :</td>
              ${this.activeTab === 'oldMetal' ? `
              <td class="text-right text-red">${formatNum(summary.amount_in.cash - summary.amount_out.cash)}</td>
              <td class="text-right text-red">${formatNum(summary.amount_in.card - summary.amount_out.card)}</td>
              <td class="text-right text-red">${formatNum(summary.amount_in.online - summary.amount_out.online)}</td>
              <td class="text-right text-red font-bold">${formatNum(netMovement)}</td>
              ` : `
              <td class="text-right text-red">${formatNum(summary.amount_in.cash - summary.amount_out.cash)}</td>
              ${this.isGst ? `
              <td class="text-right text-red">${formatNum(summary.amount_in.bank - summary.amount_out.bank)}</td>
              <td class="text-right text-red">${formatNum(summary.amount_in.card - summary.amount_out.card)}</td>
              <td class="text-right text-red">${formatNum(summary.amount_in.online - summary.amount_out.online)}</td>
              ` : ''}
              <td class="text-right text-red">${formatNum(summary.amount_in.disc - summary.amount_out.disc)}</td>
              <td class="text-right text-red">0.00</td>
              <td class="text-right text-red font-bold">${formatNum(netMovement)}</td>
              `}
            </tr>
            <tr>
              <td class="font-bold">${this.activeTab === 'oldMetal' ? 'OPENING BALANCE :' : 'OPENING BALANCE :'}</td>
              <td colspan="${this.activeTab === 'oldMetal' ? '3' : (this.isGst ? '6' : '3')}"></td>
              <td class="text-right text-red font-bold">${formatNum(openingBalance)}${this.activeTab === 'oldMetal' ? 'g' : ''}</td>
            </tr>
            <tr>
              <td class="italic">Net Movement :</td>
              <td colspan="${this.activeTab === 'oldMetal' ? '3' : (this.isGst ? '6' : '3')}"></td>
              <td class="text-right text-red font-bold">${formatNum(netMovement)}${this.activeTab === 'oldMetal' ? 'g' : ''}</td>
            </tr>
            ` : `
            <tr>
               <td class="font-bold text-0d5bab">SALE TOTALS :</td>
               <td colspan="${(this.isGst ? '6' : '3')}"></td>
               <td class="text-right font-bold text-red">${formatNum(summary.total)}</td>
            </tr>
            `}
          </tbody>
        </table>
        ` : ''}

        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() { window.close(); };
          }
        </script>
      </body>
      </html>
    `;

    const printWin = window.open('', '_blank');
    if (printWin) {
      printWin.document.open();
      printWin.document.write(printHtml);
      printWin.document.close();
    }
  }

  exportToExcel() {
    let activeComp: any = null;
    let title = 'Daybook Summary';

    if (!this.activeTab || this.activeTab === 'cashflow') {
      activeComp = this.cashflowComp;
      title = 'Daybook Summary';
    } else if (this.activeTab === 'udhaar') {
      activeComp = this.udhaarComp;
      title = 'Udhaar Summary';
    } else if (this.activeTab === 'orders') {
      activeComp = this.ordersComp;
      title = 'Order Summary';
    } else if (this.activeTab === 'oldMetal') {
      activeComp = this.oldMetalComp;
      title = 'Metal Summary';
    } else if (this.activeTab === 'purchase') {
      activeComp = this.purchaseComp;
      title = 'Purchase Summary';
    } else if (this.activeTab === 'sale') {
      activeComp = this.saleComp;
      title = 'Sale Summary';
    }

    if (!activeComp) {
      alert('Selected section data is not currently visible.');
      return;
    }

    const transactions = (this.activeTab === 'sale') ? activeComp.sales : (activeComp.transactions || []);
    const summary = (this.activeTab === 'sale') ? activeComp.saleSummary : activeComp.summary;
    const openingBalance = (this.activeTab === 'sale') ? 0 : (activeComp.openingBalance || 0);
    const firmName = this.selectedFirmName;
    const fromDate = this.fromDate || 'Start';
    const toDate = this.toDate || 'End';
    const reportPeriod = `${fromDate} to ${toDate}`;

    const exports: any[] = [];

    // 1. Transaction Header
    if (this.activeTab === 'oldMetal') {
      exports.push({ 'INV NO.': 'TRANSACTIONS', 'DATE': '', 'FIRM': '', 'USER': '', 'CITY': '', 'METAL REC.': '', 'METAL UDHAR': '', 'METAL CONV.': '', 'TOTAL SALES': '' });
    } else if (this.activeTab === 'sale') {
      exports.push({ 'INV NO.': 'TRANSACTIONS', 'DATE': '', 'CUSTOMER': '', 'TOTAL AMT': '', 'RECD AMT': '', 'DUE AMT': '', 'METAL BILLED': '', 'METAL RECD': '', 'METAL SETTLED': '', 'METAL DUE': '' });
    } else {
      const header: any = { 'INV NO.': 'TRANSACTIONS', 'DATE': '', 'FIRM': '', 'USER': '', 'CITY': '', 'CASH': '' };
      if (this.isGst) {
        header['BANK'] = '';
        header['CARD'] = '';
        header['ONLINE'] = '';
      }
      header['DISC'] = '';
      header['TOTAL'] = '';
      exports.push(header);
    }

    // 2. Add Transactions
    transactions.forEach((item: any) => {
      if (this.activeTab === 'oldMetal') {
        exports.push({
          'INV NO.': item.invoice_no,
          'DATE': item.date ? item.date.split(' ')[0] : '',
          'FIRM': item.firm,
          'USER': item.user,
          'CITY': item.city,
          'METAL REC.': parseFloat(item.cash.toString()),
          'METAL UDHAR': parseFloat(item.card.toString()),
          'METAL CONV.': parseFloat(item.online.toString()),
          'TOTAL SALES': parseFloat(item.total.toString())
        });
      } else if (this.activeTab === 'sale') {
        exports.push({
          'INV NO.': item.invoice_number,
          'DATE': item.invoice_date,
          'CUSTOMER': item.customer_name,
          'TOTAL AMT': parseFloat((item.total_amount || 0).toString()),
          'RECD AMT': parseFloat((item.amount_paid || 0).toString()),
          'DUE AMT': parseFloat((item.balance_due || 0).toString()),
          'METAL BILLED': parseFloat((item.total_metal_billed || 0).toString()),
          'METAL RECD': parseFloat((item.metal_received || 0).toString()),
          'METAL SETTLED': parseFloat((item.metal_settled || 0).toString()),
          'METAL DUE': parseFloat((item.metal_due || 0).toString())
        });
      } else {
        const row: any = {
          'INV NO.': item.invoice_no,
          'DATE': item.date ? item.date.split(' ')[0] : '',
          'FIRM': item.firm,
          'USER': item.user,
          'CITY': item.city,
          'CASH': parseFloat(item.cash.toString()),
        };
        if (this.isGst) {
          row['BANK'] = parseFloat(item.bank.toString());
          row['CARD'] = parseFloat(item.card.toString());
          row['ONLINE'] = parseFloat(item.online.toString());
        }
        row['DISC'] = parseFloat(item.disc.toString());
        row['TOTAL'] = parseFloat(item.total.toString());
        exports.push(row);
      }
    });

    exports.push({}); // Spacer

    if (this.activeTab === 'sale') {
      exports.push({
        'INV NO.': 'TOTAL:',
        'DATE': '',
        'CUSTOMER': '',
        'TOTAL AMT': parseFloat((summary.total || 0).toString()),
        'RECD AMT': parseFloat((summary.amount_received || 0).toString()),
        'DUE AMT': parseFloat((summary.balance_due || 0).toString()),
        'METAL BILLED': parseFloat((summary.metal_billed || 0).toString()),
        'METAL RECD': parseFloat((summary.metal_received || 0).toString()),
        'METAL SETTLED': parseFloat((summary.metal_settled || 0).toString()),
        'METAL DUE': parseFloat((summary.metal_due || 0).toString())
      });
      exports.push({}); // Another spacer
    }

    exports.push({}); // Spacer

    // 3. Summary Records (Amount In / Out)
    if (this.activeTab !== 'sale') {
      // 3. Summary Header
      if (this.activeTab === 'oldMetal') {
        exports.push({ 'INV NO.': 'SUMMARY', 'DATE': 'METAL REC.', 'FIRM': 'METAL UDHAR', 'USER': 'METAL CONV.', 'CITY': 'TOTAL SALES' });
      } else {
        const summaryHeader: any = { 'INV NO.': 'SUMMARY', 'DATE': 'CASH' };
        if (this.isGst) {
          summaryHeader['FIRM'] = 'BANK';
          summaryHeader['USER'] = 'CARD';
          summaryHeader['CITY'] = 'ONLINE';
        }
        summaryHeader[this.isGst ? 'CASH' : 'FIRM'] = 'DISC'; // Alignment hack
        summaryHeader[this.isGst ? 'BANK' : 'USER'] = 'EXTRA';
        summaryHeader[this.isGst ? 'CARD' : 'CITY'] = 'TOTAL';
        exports.push(summaryHeader);
      }

      // 4. Amount In
      if (this.activeTab === 'oldMetal') {
        exports.push({
          'INV NO.': 'AMOUNT IN',
          'DATE': parseFloat(summary.amount_in.cash.toString()),
          'FIRM': parseFloat(summary.amount_in.card.toString()),
          'USER': parseFloat(summary.amount_in.online.toString()),
          'CITY': parseFloat(summary.amount_in.total.toString())
        });
      } else {
        const rowIn: any = {
          'INV NO.': 'AMOUNT IN',
          'DATE': parseFloat(summary.amount_in.cash.toString()),
        };
        if (this.isGst) {
          rowIn['FIRM'] = parseFloat(summary.amount_in.bank.toString());
          rowIn['USER'] = parseFloat(summary.amount_in.card.toString());
          rowIn['CITY'] = parseFloat(summary.amount_in.online.toString());
        }
        rowIn[this.isGst ? 'CASH' : 'FIRM'] = parseFloat(summary.amount_in.disc.toString());
        rowIn[this.isGst ? 'BANK' : 'USER'] = 0;
        rowIn[this.isGst ? 'CARD' : 'CITY'] = parseFloat(summary.amount_in.total.toString());
        exports.push(rowIn);
      }

      // 5. Amount Out
      if (this.activeTab === 'oldMetal') {
        exports.push({
          'INV NO.': 'AMOUNT OUT',
          'DATE': parseFloat(summary.amount_out.cash.toString()),
          'FIRM': parseFloat(summary.amount_out.card.toString()),
          'USER': parseFloat(summary.amount_out.online.toString()),
          'CITY': parseFloat(summary.amount_out.total.toString())
        });
      } else {
        const rowOut: any = {
          'INV NO.': 'AMOUNT OUT',
          'DATE': parseFloat(summary.amount_out.cash.toString()),
        };
        if (this.isGst) {
          rowOut['FIRM'] = parseFloat(summary.amount_out.bank.toString());
          rowOut['USER'] = parseFloat(summary.amount_out.card.toString());
          rowOut['CITY'] = parseFloat(summary.amount_out.online.toString());
        }
        rowOut[this.isGst ? 'CASH' : 'FIRM'] = parseFloat(summary.amount_out.disc.toString());
        rowOut[this.isGst ? 'BANK' : 'USER'] = 0;
        rowOut[this.isGst ? 'CARD' : 'CITY'] = parseFloat(summary.amount_out.total.toString());
        exports.push(rowOut);
      }

      // 6. Today Total
      if (this.activeTab === 'oldMetal') {
        exports.push({
          'INV NO.': 'TODAY TOTAL',
          'DATE': parseFloat((summary.amount_in.cash - summary.amount_out.cash).toString()),
          'FIRM': parseFloat((summary.amount_in.card - summary.amount_out.card).toString()),
          'USER': parseFloat((summary.amount_in.online - summary.amount_out.online).toString()),
          'CITY': parseFloat((summary.amount_in.total - summary.amount_out.total).toString())
        });
      } else {
        const rowToday: any = {
          'INV NO.': 'TODAY TOTAL',
          'DATE': parseFloat((summary.amount_in.cash - summary.amount_out.cash).toString()),
        };
        if (this.isGst) {
          rowToday['FIRM'] = parseFloat((summary.amount_in.bank - summary.amount_out.bank).toString());
          rowToday['USER'] = parseFloat((summary.amount_in.card - summary.amount_out.card).toString());
          rowToday['CITY'] = parseFloat((summary.amount_in.online - summary.amount_out.online).toString());
        }
        rowToday[this.isGst ? 'CASH' : 'FIRM'] = parseFloat((summary.amount_in.disc - summary.amount_out.disc).toString());
        rowToday[this.isGst ? 'BANK' : 'USER'] = 0;
        rowToday[this.isGst ? 'CARD' : 'CITY'] = parseFloat((summary.amount_in.total - summary.amount_out.total).toString());
        exports.push(rowToday);
      }

      exports.push({}); // Spacer

      // 7. Final Totals
      const netMovement = (openingBalance === 0) ? (summary.amount_in.total - summary.amount_out.total) : openingBalance; // Simplified
      const closingBalance = openingBalance + (summary.amount_in.total - summary.amount_out.total);

      exports.push({ 'INV NO.': 'OPENING BALANCE', 'DATE': parseFloat(openingBalance.toString()) });
      exports.push({ 'INV NO.': 'NET MOVEMENT', 'DATE': parseFloat((summary.amount_in.total - summary.amount_out.total).toString()) });
      exports.push({ 'INV NO.': 'CLOSING BALANCE', 'DATE': parseFloat(closingBalance.toString()) });
      exports.push({ 'INV NO.': 'FINAL TOTAL', 'DATE': parseFloat(closingBalance.toString()) });
    }

    // Initialize Worksheet
    const worksheet = XLSX.utils.json_to_sheet([]);

    // Add Professional Header Rows
    XLSX.utils.sheet_add_aoa(worksheet, [
      [`${firmName.toUpperCase()} - ${title.toUpperCase()}`],
      [`PERIOD: ${reportPeriod}`],
      [`GENERATED ON: ${new Date().toLocaleString('en-GB')}`],
      [] // Spacer
    ], { origin: "A1" });

    // Add Table Data
    XLSX.utils.sheet_add_json(worksheet, exports, { origin: "A5", skipHeader: false });

    // Set column widths
    if (this.activeTab === 'oldMetal') {
      worksheet['!cols'] = [
        { wch: 15 }, // INV NO
        { wch: 15 }, // DATE
        { wch: 25 }, // FIRM
        { wch: 20 }, // USER
        { wch: 15 }, // CITY
        { wch: 15 }, // METAL REC.
        { wch: 15 }, // METAL UDHAR
        { wch: 15 }, // METAL CONV.
        { wch: 15 }, // TOTAL SALES
      ];
    } else if (this.activeTab === 'sale') {
      worksheet['!cols'] = [
        { wch: 15 }, // INV NO
        { wch: 15 }, // DATE
        { wch: 25 }, // CUSTOMER
        { wch: 15 }, // TOTAL AMT
        { wch: 15 }, // RECD AMT
        { wch: 15 }, // DUE AMT
        { wch: 15 }, // METAL BILLED
        { wch: 15 }, // METAL RECD
        { wch: 15 }, // METAL SETTLED
        { wch: 15 }, // METAL DUE
      ];
    } else {
      worksheet['!cols'] = [
        { wch: 15 }, // INV NO
        { wch: 15 }, // DATE / CASH
        { wch: 25 }, // FIRM / BANK
        { wch: 20 }, // USER / CARD
        { wch: 15 }, // CITY / ONLINE
        { wch: 12 }, // CASH / DISC
        { wch: 12 }, // BANK / EXTRA
        { wch: 12 }, // CARD / TOTAL
        { wch: 12 }, // ONLINE
        { wch: 10 }, // DISC
        { wch: 15 }, // TOTAL
      ];
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Daybook');

    const fileName = `${title.replace(/\s+/g, '_')}_${firmName.replace(/\s+/g, '_')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }
}
